/**
 * --------------------------------------------------------------------------
 * Predictive Search Component
 * --------------------------------------------------------------------------
 * Handles predictive search functionality using Shopify's Predictive Search API.
 * Displays search suggestions as the user types, with configurable resource types
 * and result limits.
 */
if (!customElements.get('predictive-search')) {
    class PredictiveSearch extends HTMLElement {
        static CONSTANTS = {
            DEBOUNCE_DELAY: 300,
            MIN_QUERY_LENGTH: 1,
            API_ENDPOINT: '/search/suggest.json'
        };

        constructor() {
            super();
            this.searchInput = null;
            this.searchForm = null;
            this.resultsContainer = null;
            this.isEnabled = false;
            this.currentRequest = null;
            this.currentQuery = null; // Track the current query being searched
        }

        connectedCallback() {
            if (this.initialize()) {
                this.addEventListeners();
            }
        }

        disconnectedCallback() {
            this.removeEventListeners();
        }

        // --------------------------------------------------------------------
        // SETUP & INITIALIZATION
        // --------------------------------------------------------------------

        /**
         * Initializes the component and validates required elements.
         * @returns {boolean} True if initialization is successful, false otherwise.
         */
        initialize() {
            // Try to find input with data-search-input attribute first (within component)
            this.searchInput = this.querySelector('[data-search-input]');
            // Fallback: find input with name="q" (search query parameter)
            if (!this.searchInput) {
                this.searchInput = this.querySelector('input[name="q"]');
            }
            // Additional fallback: find any search input type
            if (!this.searchInput) {
                this.searchInput = this.querySelector('input[type="search"]');
            }
            
            // If still not found, check in open dialogs/popovers (for modal search)
            let openDialog = null;
            if (!this.searchInput) {
                openDialog = document.querySelector('[popover]:popover-open, [popover][open]');
                if (openDialog) {
                    this.searchInput = openDialog.querySelector('[data-search-input]') ||
                                      openDialog.querySelector('input[name="q"]') ||
                                      openDialog.querySelector('input[type="search"]');
                }
            }
            
            this.searchForm = this.querySelector('[data-search-form]');
            if (!this.searchForm && this.searchInput) {
                this.searchForm = this.searchInput.closest('form');
            }
            this.resultsContainer = this.querySelector('[data-search-results]');
            // If results container not found, check in dialog
            if (!this.resultsContainer) {
                if (!openDialog) {
                    openDialog = document.querySelector('[popover]:popover-open, [popover][open]');
                }
                if (openDialog) {
                    this.resultsContainer = openDialog.querySelector('[data-search-results]');
                }
            }

            if (!this.searchInput) {
                console.error('PredictiveSearch Error: Could not find search input. Looked for [data-search-input], input[name="q"], and input[type="search"].', this);
                return false;
            }

            if (!this.searchForm) {
                console.error('PredictiveSearch Error: Could not find search form with [data-search-form] attribute.', this);
                return false;
            }

            if (!this.resultsContainer) {
                console.warn('PredictiveSearch Warning: No results container found. Predictive search will be disabled.', this);
                return false;
            }

            this.isEnabled = true;
            return true;
        }

        /**
         * Adds all necessary event listeners.
         */
        addEventListeners() {
            if (!this.isEnabled) return;

            this.handleInput = this.handleInput.bind(this);
            this.handleSubmit = this.handleSubmit.bind(this);
            this.handleClickOutside = this.handleClickOutside.bind(this);
            this.handleKeyDown = this.handleKeyDown.bind(this);

            this.searchInput.addEventListener('input', this.handleInput);
            this.searchForm.addEventListener('submit', this.handleSubmit);
            document.addEventListener('click', this.handleClickOutside);
            this.searchInput.addEventListener('keydown', this.handleKeyDown);
            // Also attach to component to handle navigation when results are focused
            this.addEventListener('keydown', this.handleKeyDown);
        }

        /**
         * Cleans up all event listeners.
         */
        removeEventListeners() {
            if (this.searchInput) {
                this.searchInput.removeEventListener('input', this.handleInput);
                this.searchInput.removeEventListener('keydown', this.handleKeyDown);
            }
            if (this.searchForm) {
                this.searchForm.removeEventListener('submit', this.handleSubmit);
            }
            document.removeEventListener('click', this.handleClickOutside);
            this.removeEventListener('keydown', this.handleKeyDown);
        }

        // --------------------------------------------------------------------
        // EVENT HANDLERS
        // --------------------------------------------------------------------

        /**
         * Handles input events on the search field with debouncing.
         * @param {Event} event
         */
        handleInput(event) {
            if (!this.isEnabled) return;

            const query = event.target.value.trim();

            // If query is empty, hide results container (loader is child, so it hides too)
            if (query.length === 0) {
                this.currentQuery = null; // Clear current query
                this.hideResults();
                return;
            }

            // Step 1: Show results container immediately (must be visible before showing loader)
            this.showResultsContainer();

            // Step 2: Show loading overlay (now that container is visible)
            this.showLoading();

            // Only perform search if query meets minimum length
            if (query.length < PredictiveSearch.CONSTANTS.MIN_QUERY_LENGTH) {
                return;
            }

            // Debounce the search request
            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout);
            }

            this.debounceTimeout = setTimeout(() => {
                this.performSearch(query);
            }, PredictiveSearch.CONSTANTS.DEBOUNCE_DELAY);
        }

        /**
         * Handles form submission.
         * @param {Event} event
         */
        handleSubmit(event) {
            // Check if "Show View All Results" is disabled
            const showViewAllResults = this.getConfig('show-view-all-results', 'false') === 'true';
            
            // If disabled, prevent form submission (don't navigate to search results page)
            if (!showViewAllResults) {
                event.preventDefault();
            }
            
            // Hide results in all cases
            this.hideResults();
        }

        /**
         * Handles clicks outside the search component to hide results.
         * @param {Event} event
         */
        handleClickOutside(event) {
            if (!this.isEnabled) return;

            const isClickInside = this.contains(event.target);
            if (!isClickInside && this.resultsContainer.classList.contains('!block')) {
                this.hideResults();
            }
        }

        /**
         * Handles keyboard navigation in search results.
         * @param {KeyboardEvent} event
         */
        handleKeyDown(event) {
            // Only handle keyboard navigation when results are visible
            const resultsVisible = this.isEnabled && this.resultsContainer && this.resultsContainer.classList.contains('!block');
            
            if (!resultsVisible && event.key !== 'ArrowDown') {
                return;
            }

            // Get all focusable elements (suggestions and product links)
            const focusableElements = resultsVisible ? this.resultsContainer.querySelectorAll('a, button.predictive-search-suggestion') : [];
            
            // Handle ArrowDown from input field when results are visible
            if (event.key === 'ArrowDown' && resultsVisible && focusableElements.length > 0) {
                if (document.activeElement === this.searchInput) {
                    event.preventDefault();
                    // Focus first result when ArrowDown pressed from input
                    focusableElements[0]?.focus();
                    return;
                }
            }

            if (!resultsVisible || focusableElements.length === 0) {
                return;
            }

            const currentIndex = Array.from(focusableElements).indexOf(document.activeElement);

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    // If no element is focused, start at first
                    // Otherwise, move to next (wrap to first if at end)
                    const nextIndex = currentIndex < 0 ? 0 : (currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0);
                    focusableElements[nextIndex]?.focus();
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    // If no element is focused, start at last
                    // Otherwise, move to previous (wrap to last if at start)
                    const prevIndex = currentIndex < 0 ? focusableElements.length - 1 : (currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1);
                    focusableElements[prevIndex]?.focus();
                    break;
                case 'Enter':
                    // If a focusable element is focused, trigger its click
                    if (document.activeElement && Array.from(focusableElements).includes(document.activeElement)) {
                        event.preventDefault();
                        document.activeElement.click();
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    this.hideResults();
                    this.searchInput.focus();
                    break;
            }
        }

        // --------------------------------------------------------------------
        // SEARCH FUNCTIONALITY
        // --------------------------------------------------------------------

        /**
         * Performs a predictive search request using Shopify's Predictive Search API.
         * @param {string} query The search query.
         */
        async performSearch(query) {
            if (this.currentRequest) {
                this.currentRequest.abort();
            }

            // Store the current query for this search request
            this.currentQuery = query;

            const params = this.buildSearchParams(query);
            // Shopify's predictive search API uses /search/suggest.json endpoint
            // Use relative path for fetch (works with same-origin requests)
            const url = `${PredictiveSearch.CONSTANTS.API_ENDPOINT}?${params}`;

            try {
                this.currentRequest = new AbortController();
                const response = await fetch(url, {
                    signal: this.currentRequest.signal
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();
                // Only display results if the query hasn't changed since the request started
                if (this.currentQuery === query) {
                    this.displayResults(data, query);
                }
            } catch (error) {
                if (error.name === 'AbortError') {
                    // Request was aborted, ignore
                    return;
                }
                console.error('PredictiveSearch Error: Failed to fetch search results.', error);
                this.hideResults();
            } finally {
                this.currentRequest = null;
            }
        }

        /**
         * Builds the search parameters based on component configuration.
         * Uses Shopify's predictive search API.
         * @param {string} query The search query.
         * @returns {string} The URL-encoded parameters.
         */
        buildSearchParams(query) {
            // Include query in resources type to get suggestions
            const resourceTypes = this.getConfig('resources_type', 'product,collection');
            const params = new URLSearchParams({
                q: query,
                'resources[type]': `query,${resourceTypes}`,
                'resources[limit]': this.getConfig('resources_limit', '5'),
                'resources[limit_scope]': this.getConfig('limit-scope', 'all'),
                'resources[options][unavailable_products]': this.getConfig('unavailable-products', 'last')
            });

            return params.toString();
        }

        /**
         * Gets a configuration value from data attributes.
         * @param {string} key The configuration key.
         * @param {string} defaultValue The default value if not found.
         * @returns {string} The configuration value.
         */
        getConfig(key, defaultValue) {
            const dataKey = key.replace(/_/g, '-');
            return this.dataset[dataKey] || defaultValue;
        }

        /**
         * Displays the search results matching the Figma design.
         * Shopify's predictive search API returns JSON with resources object.
         * @param {Object} data The JSON response from the API.
         * @param {string} query The search query.
         */
        displayResults(data, query) {
            if (!this.resultsContainer) return;

            try {
                // Check if the current input value matches the query for these results
                // If the user has cleared or changed the search, don't display stale results
                const currentInputValue = this.searchInput?.value.trim() || '';
                if (currentInputValue !== query) {
                    // Query has changed since this request was made, don't display results
                    return;
                }

                // Shopify API returns queries and products in different structures
                // Check both possible locations for queries
                const resources = data.resources?.results || data.resources;
                const queries = data.resources?.results?.queries || data.resources?.queries || data.queries || [];
                
                // Check if we have any results (products or queries)
                const hasResults = this.hasAnyResults(resources) || queries.length > 0;
                
                // Build HTML matching Figma design exactly - using standard Tailwind classes
                // px-8 = 32px, py-6 = 24px, gap-6 = 24px, gap-3 = 12px
                // Add relative positioning for spinner overlay and predictive-search-content class
                let html = '<div class="predictive-search-content box-border flex flex-col gap-6 items-start px-4 md:px-8 py-6 w-full relative">';
                
                // If no results, show "No results found" message (centrally aligned)
                if (!hasResults) {
                    html += '<div class="flex items-center justify-center w-full py-6">';
                    html += '<p class="text-base text-black font-style-heading-5">No results found</p>';
                    html += '</div>';
                    html += '</div>';
                    this.resultsContainer.innerHTML = html;
                    this.resultsContainer.classList.remove('hidden');
                    this.resultsContainer.classList.add('!block');
                    // Hide loading state after showing "No results found"
                    this.hideLoading();
                    return;
                }

                // SUGGESTIONS Section - must come first
                if (queries.length > 0) {
                    html += '<div class="flex flex-col gap-3 items-start relative shrink-0 w-full">';
                    
                    // Section Header with underline
                    html += '<div class="flex flex-col gap-3 items-start relative shrink-0 w-full">';
                    html += '<div class="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-black w-full">';
                    html += '<p class="font-style-body-3 uppercase">Suggestions</p>';
                    html += '</div>';
                    html += '<div class="h-0 relative shrink-0 w-full">';
                    html += '<div class="absolute bottom-[-0.5px] left-0 right-0 top-[-0.5px] border-b border-black"></div>';
                    html += '</div>';
                    html += '</div>';
                    
                    // Query Suggestions List
                    html += '<div class="flex flex-col gap-3 items-start leading-[0] not-italic relative shrink-0 text-base text-black w-full">';
                    queries.slice(0, 5).forEach(queryItem => {
                        // Handle different query response formats
                        const queryText = queryItem.text || queryItem.title || queryItem.query || queryItem;
                        html += `<button type="button" class="predictive-search-suggestion flex flex-col justify-center relative shrink-0 w-full hover:opacity-70 transition-opacity text-left focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2" data-query="${this.escapeHtml(queryText)}" tabindex="0">`;
                        html += `<p class="font-style-heading-5">${this.escapeHtml(queryText)}</p>`;
                        html += '</button>';
                    });
                    html += '</div>';
                    
                    html += '</div>';
                }

                // PRODUCTS Section
                if (resources.products?.length > 0) {
                    html += '<div class="flex flex-col gap-3 items-start relative shrink-0 w-full">';
                    
                    // Section Header with underline
                    html += '<div class="flex flex-col gap-3 items-start relative shrink-0 w-full">';
                    html += '<div class="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-[10px] text-black w-full">';
                    html += '<p class="font-style-body-3 uppercase">Products</p>';
                    html += '</div>';
                    html += '<div class="h-0 relative shrink-0 w-full">';
                    html += '<div class="absolute bottom-[-0.5px] left-0 right-0 top-[-0.5px] border-b border-black"></div>';
                    html += '</div>';
                    html += '</div>';
                    
                    // Products List
                    html += '<div class="flex flex-col gap-3 items-start relative shrink-0 w-full">';
                    resources.products.slice(0, 5).forEach(product => {
                        const productImage = product.image || product.featured_media?.preview_image || '';
                        const productImageUrl = productImage ? `${productImage}?width=64` : '';
                        html += `<a href="${product.url}" class="flex gap-3 items-center relative shrink-0 w-full hover:opacity-70 transition-opacity focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2" tabindex="0">`;
                        
                        // Product Image (32px x 32px) - using w-8 h-8
                        html += '<div class="relative shrink-0 w-8 h-8">';
                        if (productImageUrl) {
                            html += `<img src="${productImageUrl}" alt="${this.escapeHtml(product.title)}" width="32" height="32" class="absolute inset-0 max-w-none object-center object-cover pointer-events-none w-full h-full" loading="lazy">`;
                        } else {
                            html += '<div class="w-full h-full bg-gray-100"></div>';
                        }
                        html += '</div>';
                        
                        // Product Title
                        html += '<div class="flex flex-col justify-center leading-[0] not-italic relative shrink-0 text-base text-black whitespace-nowrap">';
                        html += `<p class="font-style-heading-5 whitespace-pre">${this.escapeHtml(product.title)}</p>`;
                        html += '</div>';
                        
                        html += '</a>';
                    });
                    html += '</div>';
                    
                    html += '</div>';
                }

                // Footer with View All Results and Press Enter (only if enabled)
                const showViewAll = this.getConfig('show-view-all-results', 'false') === 'true';
                if (showViewAll) {
                    html += '<div class="flex flex-col gap-3 items-start relative shrink-0 w-full">';
                    html += '<div class="h-0 relative shrink-0 w-full">';
                    html += '<div class="absolute bottom-[-0.5px] left-0 right-0 top-[-0.5px] border-b border-black"></div>';
                    html += '</div>';
                    html += '<div class="flex items-start justify-between leading-[0] not-italic relative shrink-0 text-xs text-black whitespace-nowrap w-full">';
                    
                    const viewAllUrl = `/search?q=${encodeURIComponent(query)}`;
                    html += `<a href="${viewAllUrl}" class="flex flex-col justify-center relative shrink-0">`;
                    html += '<p class="decoration-solid whitespace-pre underline">View All Results</p>';
                    html += '</a>';
                    
                    html += '<div class="flex flex-col justify-center relative shrink-0">';
                    html += '<p class=" whitespace-pre">Press Enter</p>';
                    html += '</div>';
                    html += '</div>';
                    html += '</div>';
                }

                html += '</div>';
                
                // Insert the HTML into the results container
                this.resultsContainer.innerHTML = html;
                this.resultsContainer.classList.remove('hidden');
                this.resultsContainer.classList.add('!block');
                
                // Attach click handlers to suggestion buttons
                this.attachSuggestionHandlers();
                
                // Hide loading state after results are displayed
                this.hideLoading();
            } catch (error) {
                console.error('PredictiveSearch Error: Failed to display search results.', error);
                this.hideResults();
            }
        }

        /**
         * Checks if there are any results to display.
         * @param {Object} resources The search results resources.
         * @returns {boolean} True if there are results, false otherwise.
         */
        hasAnyResults(resources) {
            if (!resources) return false;
            return (
                (resources.products && resources.products.length > 0) ||
                (resources.collections && resources.collections.length > 0) ||
                (resources.pages && resources.pages.length > 0) ||
                (resources.articles && resources.articles.length > 0) ||
                (resources.queries && resources.queries.length > 0)
            );
        }

        /**
         * Escapes HTML special characters to prevent XSS.
         * @param {string} text The text to escape.
         * @returns {string} The escaped text.
         */
        escapeHtml(text) {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
        }

        /**
         * Shows the results container (makes it visible).
         */
        showResultsContainer() {
            if (!this.resultsContainer) return;
            this.resultsContainer.classList.remove('hidden');
            this.resultsContainer.classList.add('!block');
        }

        /**
         * Shows loading state with spinner overlay inside the results content.
         * NOTE: Results container MUST be visible before calling this (loader is a child of it).
         */
        showLoading() {
            if (!this.resultsContainer) return;
                
            // Get or create spinner overlay inside the content div
            let spinner = this.resultsContainer.querySelector('.predictive-search-spinner');
            if (!spinner) {
                spinner = document.createElement('div');
                spinner.className = 'predictive-search-spinner absolute inset-0 flex items-center justify-center z-10 bg-white/50 backdrop-blur-sm';
                this.resultsContainer.appendChild(spinner);
                // Create spinner HTML
                spinner.innerHTML = `
                    <div class="flex flex-col items-center justify-center">
                        <div class="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                    </div>
                `;
            }
            
            spinner.classList.remove('hidden');
        }

        /**
         * Attaches click handlers to suggestion buttons to set input value and search inline.
         */
        attachSuggestionHandlers() {
            if (!this.resultsContainer) return;
            
            const suggestionButtons = this.resultsContainer.querySelectorAll('.predictive-search-suggestion');
            suggestionButtons.forEach(button => {
                button.addEventListener('click', (e) => {
                    e.preventDefault();
                    const query = button.getAttribute('data-query');
                    if (query && this.searchInput) {
                        // Set the input value
                        this.searchInput.value = query;
                        
                        // Trigger input event to run the search inline
                        const inputEvent = new Event('input', { bubbles: true });
                        this.searchInput.dispatchEvent(inputEvent);
                    }
                });
            });
        }

        /**
         * Hides loading state and removes blur.
         */
        hideLoading() {
            if (!this.resultsContainer) return;
            
            // Get content div
            let contentDiv = this.resultsContainer.querySelector('.predictive-search-content');
            if (!contentDiv) {
                contentDiv = this.resultsContainer.querySelector('div:not(.predictive-search-spinner)');
            }
            
            if (contentDiv) {
                // Remove blur effect from content
                contentDiv.classList.remove('blur-sm', 'opacity-50');
                
                // Hide spinner overlay inside content
                const spinner = contentDiv.querySelector('.predictive-search-spinner');
                if (spinner) {
                    spinner.classList.add('hidden');
                }
            }
        }

        /**
         * Hides the search results.
         */
        hideResults() {
            if (this.resultsContainer) {
                this.hideLoading();
                this.resultsContainer.classList.add('hidden');
                this.resultsContainer.classList.remove('!block');
                this.resultsContainer.innerHTML = '';
            }
        }

        // --------------------------------------------------------------------
        // UTILITY METHODS
        // --------------------------------------------------------------------

        /**
         * Formats a price value using Shopify's money format.
         * @param {number|string} price The price to format.
         * @returns {string} The formatted price.
         */
        static formatPrice(price) {
            if (typeof window.theme?.utils?.money === 'function') {
                return window.theme.utils.money(price);
            }
            
            // Fallback formatting
            const numPrice = parseFloat(price);
            if (isNaN(numPrice)) return '$0.00';
            return `$${numPrice.toFixed(2)}`;
        }
    }

    customElements.define('predictive-search', PredictiveSearch);
}

